import { Link, useLocation } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// project imports
import Profile from './Profile';
import Notification from './Notification';
import MobileSection from './MobileSection';
import navigation from 'menu-items';

// Known route definitions with exact page titles and breadcrumb trails
const ROUTE_CONFIG = {
  '/': {
    title: 'Dashboard',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Dashboard' }]
  },
  '/dashboard': {
    title: 'Dashboard',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Dashboard' }]
  },
  '/dashboard/default': {
    title: 'Dashboard',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Dashboard' }]
  },
  '/management/clients': {
    title: 'Clients',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Management' }, { title: 'Clients' }]
  },
  '/management/sites': {
    title: 'Sites',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Management' }, { title: 'Sites' }]
  },
  '/management/licenses': {
    title: 'Licenses - All Licenses',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Licenses' }, { title: 'All Licenses' }]
  },
  '/management/licenses/history': {
    title: 'Licenses - License History',
    crumbs: [
      { title: 'Home', to: '/' },
      { title: 'Licenses', to: '/management/licenses' },
      { title: 'History' }
    ]
  },
  '/management/audits': {
    title: 'Audits',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Management' }, { title: 'Audits' }]
  },
  '/management/audits/new': {
    title: 'Audits - New Electrical Safety Audit',
    crumbs: [
      { title: 'Home', to: '/' },
      { title: 'Audits', to: '/management/audits' },
      { title: 'New Audit' }
    ]
  },
  '/reports': {
    title: 'Reports - Reports & Analytics',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Reports & Notifications' }, { title: 'Reports' }]
  },
  '/reports/graph': {
    title: 'Reports - Hierarchical Graph View',
    crumbs: [
      { title: 'Home', to: '/' },
      { title: 'Reports & Notifications', to: '/reports' },
      { title: 'Graph View' }
    ]
  },
  '/notifications': {
    title: 'Notifications',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Reports & Notifications' }, { title: 'Notifications' }]
  },
  '/user-management/users': {
    title: 'User Management - Manage Users',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'User Management' }, { title: 'Manage Users' }]
  },
  '/user-management/roles': {
    title: 'User Management - Manage Roles',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'User Management' }, { title: 'Manage Roles' }]
  },
  '/user-management/access-control': {
    title: 'User Management - Access Control',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'User Management' }, { title: 'Access Control' }]
  },
  '/typography': {
    title: 'Typography',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Components' }, { title: 'Typography' }]
  },
  '/color': {
    title: 'Color',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Components' }, { title: 'Color' }]
  },
  '/shadow': {
    title: 'Shadow',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Components' }, { title: 'Shadow' }]
  },
  '/sample-page': {
    title: 'Sample Page',
    crumbs: [{ title: 'Home', to: '/' }, { title: 'Sample Page' }]
  }
};

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const location = useLocation();

  const getPageInfo = (pathname) => {
    let customLocation = pathname;
    if (customLocation === '') customLocation = '/';

    if (ROUTE_CONFIG[customLocation]) {
      return ROUTE_CONFIG[customLocation];
    }

    // Dynamic menu traversal fallback
    if (navigation?.items) {
      for (const group of navigation.items) {
        if (group.url === customLocation) {
          return {
            title: group.title,
            crumbs: [{ title: 'Home', to: '/' }, { title: group.title }]
          };
        }
        if (group.children) {
          for (const child of group.children) {
            if (child.url === customLocation) {
              const crumbs = [{ title: 'Home', to: '/' }];
              if (group.title && group.title !== 'Navigation') {
                crumbs.push({ title: group.title });
              }
              crumbs.push({ title: child.title });
              const fullTitle = (group.type === 'collapse' && group.title)
                ? `${group.title} - ${child.title}`
                : child.title;
              return { title: fullTitle, crumbs };
            }
            if (child.children) {
              for (const subChild of child.children) {
                if (subChild.url === customLocation) {
                  const crumbs = [{ title: 'Home', to: '/' }];
                  if (group.title && group.title !== 'Navigation') {
                    crumbs.push({ title: group.title });
                  }
                  crumbs.push({ title: child.title, to: child.url });
                  crumbs.push({ title: subChild.title });
                  const menuName = child.title || group.title;
                  const fullTitle = menuName ? `${menuName} - ${subChild.title}` : subChild.title;
                  return { title: fullTitle, crumbs };
                }
              }
            }
          }
        }
      }
    }

    // Pathname segments fallback
    const segments = customLocation.split('/').filter(Boolean);
    if (segments.length > 0) {
      const crumbs = [{ title: 'Home', to: '/' }];
      let currentPath = '';
      segments.forEach((seg, idx) => {
        currentPath += `/${seg}`;
        const formatted = seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ');
        const isLast = idx === segments.length - 1;
        crumbs.push({
          title: formatted,
          to: isLast ? null : currentPath
        });
      });
      const last = segments[segments.length - 1];
      return {
        title: last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' '),
        crumbs
      };
    }

    return { title: '', crumbs: [] };
  };

  const { title, crumbs } = getPageInfo(location.pathname);

  return (
    <>
      {/* Center-Aligned Title & Breadcrumbs in the Top Bar */}
      {title && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            maxWidth: { xs: '52%', sm: '64%', md: '72%' },
            zIndex: 1
          }}
        >
          {/* Page Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#0f172a',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.25px',
              fontSize: { xs: '0.92rem', sm: '1.12rem' },
              lineHeight: 1.2
            }}
          >
            {title}
          </Typography>

          {/* Breadcrumbs with reduced font size */}
          {crumbs && crumbs.length > 0 && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'nowrap',
                overflow: 'hidden',
                maxWidth: '100%',
                mt: '2px',
                lineHeight: 1
              }}
            >
              {crumbs.map((crumb, idx) => {
                const isLast = idx === crumbs.length - 1;
                return (
                  <Box
                    key={idx}
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      flexShrink: isLast ? 0 : 1,
                      overflow: 'hidden'
                    }}
                  >
                    {idx > 0 && (
                      <Typography
                        component="span"
                        sx={{
                          color: '#94a3b8',
                          fontSize: { xs: '0.58rem', sm: '0.62rem' },
                          mx: { xs: '3px', sm: '5px' },
                          userSelect: 'none'
                        }}
                      >
                        /
                      </Typography>
                    )}
                    {crumb.to && !isLast ? (
                      <Box
                        component={Link}
                        to={crumb.to}
                        sx={{
                          textDecoration: 'none',
                          color: '#64748b',
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          fontWeight: 500,
                          transition: 'color 0.15s ease',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '&:hover': {
                            color: '#1677ff'
                          }
                        }}
                      >
                        {crumb.title}
                      </Box>
                    ) : (
                      <Typography
                        component="span"
                        sx={{
                          color: isLast ? '#1e293b' : '#64748b',
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          fontWeight: isLast ? 600 : 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {crumb.title}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Spacer to push actions to the right */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Right Actions */}
      <Notification />
      {!downLG && <Profile />}
      {downLG && <MobileSection />}
    </>
  );
}
